import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProducts } from '../context/ProductsContext';
import { useSiteContent, useCollectionPages } from '../context/SiteContentContext';
import { getPageImage } from '../lib/imageStore';
import GuyanaStripe from '../components/layout/GuyanaStripe';
import PageTitle from '../components/ui/PageTitle';
import ProductCard from '../components/ui/ProductCard';
import { pageProducts as productsForPage } from '../lib/categoryPages';
import './CollectionPage.css';

export default function CollectionPage() {
  var params = useParams();
  var slug = params.slug;
  var productsCtx = useProducts();
  var allProducts = productsCtx.products;
  var contentLoading = useSiteContent().loading;
  var page = useCollectionPages().bySlug(slug);
  // The chosen category belongs to one collection; moving to another page resets it.
  var [category, setCategory] = useState({ slug: null, value: null });

  // A page added in the admin may only exist in Supabase: wait for it before saying 404.
  if (productsCtx.loading || (!page && contentLoading)) {
    return <div style={{ padding: '120px 40px', textAlign: 'center', color: '#A89880' }}>Loading...</div>;
  }

  if (!page || page.status !== 'live') {
    return (
      <div style={{ padding: '80px 40px', textAlign: 'center', minHeight: '50vh' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>404</div>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: '#1A1A1A', marginBottom: '8px' }}>Page not found</h1>
        <p style={{ color: '#6B3F22', fontSize: '14px', marginBottom: '24px' }}>This collection may have been removed.</p>
        <Link to="/shop" style={{ background: '#C8973A', color: '#1E3A2F', padding: '11px 22px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', borderRadius: '2px', display: 'inline-block' }}>Browse all products</Link>
      </div>
    );
  }

  var heroImg = getPageImage('collection_' + page.id);

  // Live products of the page's category, plus its hand-picked extras
  var pageProducts = productsForPage(page, allProducts);

  // Category chips, only when the collection holds more than one kind of piece.
  var categories = [];
  pageProducts.forEach(function(product) {
    if (product.category && categories.indexOf(product.category) < 0) categories.push(product.category);
  });
  var activeCategory = category.slug === slug && categories.indexOf(category.value) >= 0 ? category.value : null;
  var shownProducts = activeCategory
    ? pageProducts.filter(function(product) { return product.category === activeCategory; })
    : pageProducts;

  return (
    <>
      <PageTitle title={page.name} />

      <section className={'coll-hero' + (heroImg ? ' coll-hero--has-img' : '')}>
        {heroImg && <div className="coll-hero__bg"><img src={heroImg} alt={page.name} /></div>}
        <div className="coll-hero__content">
          {page.eyebrow && <span className="coll-hero__eyebrow">{page.eyebrow}</span>}
          <h1 className="coll-hero__title">{page.name}</h1>
          {page.description && <p className="coll-hero__desc">{page.description}</p>}
        </div>
      </section>

      <GuyanaStripe />

      {pageProducts.length === 0 ? (
        <div className="coll-empty">
          <h2>New pieces coming soon</h2>
          <p>Each piece is made by hand, so new ones arrive here over time. In the meantime, have a look around the rest of the shop.</p>
          <Link to="/shop" className="coll-empty__btn">Browse all products</Link>
        </div>
      ) : (
        <div className="container coll-body">
          <div className="coll-toolbar">
            <span className="coll-count">
              {shownProducts.length + (shownProducts.length === 1 ? ' piece' : ' pieces')}
            </span>
            {categories.length > 1 && (
              <div className="coll-filters" role="group" aria-label="Filter by category">
                {['All'].concat(categories).map(function(cat) {
                  var active = cat === 'All' ? !activeCategory : activeCategory === cat;
                  return (
                    <button
                      type="button"
                      key={cat}
                      className={'coll-filter' + (active ? ' coll-filter--active' : '')}
                      aria-pressed={active}
                      onClick={function() { setCategory({ slug: slug, value: cat === 'All' ? null : cat }); }}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="product-grid coll-grid">
            {shownProducts.map(function(product) {
              return <ProductCard product={product} key={product.id} />;
            })}
          </div>
        </div>
      )}
    </>
  );
}